import { useCallback, useEffect, useState } from 'react';
import { ncfAPI } from '../../services/api';
import Typeahead from '../Typeahead';

const EMAIL_TYPES = [
  { key: 'new', label: 'New submission' },
  { key: 'updated', label: 'Updated submission' },
  { key: 'review', label: 'Review request' },
  { key: 'start_date_reminder', label: 'Start date reminder' },
  { key: 'start_date_verified', label: 'Start date verified' },
  { key: 'comment', label: 'Comment posted' }
];

const DEFAULT_EMAIL_FIELDS = { new: [], updated: [], review: [], start_date_reminder: [], start_date_verified: [], comment: [] };
const DEFAULT_REMINDER_SCHEDULE = { enabled: true, intervalDays: 1, weekdays: [0, 1, 2, 3, 4, 5, 6], timeEastern: '08:00' };
const WEEKDAYS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' }
];

// Placeholder values for the live sample preview below - these are display-only and never sent.
const SAMPLE_FIELD_VALUES = {
  'personal.firstName': 'Jane',
  'personal.lastName': 'Doe',
  'ncf.submissionReason': 'New Hire',
  'ncf.submittedBy': 'Sarah Barnosky',
  'assignment.clientName': 'Acme Corp',
  'assignment.jobTitle': 'Software Engineer',
  'assignment.payRate': '45',
  'assignment.desiredStartDate': '2026-09-22'
};
const SAMPLE_CANDIDATE_NAME = 'Jane Doe';
const SAMPLE_CLIENT_NAME = 'Acme Corp';
const SAMPLE_DATE = '2026-09-22';
const SAMPLE_DETAIL_URL = 'https://toolbox.rsc.com/ncfs/123';

const buildSampleFieldLines = (selectedFields, fieldsList) => selectedFields
  .map(sourcePath => {
    const label = fieldsList.find(field => field.sourcePath === sourcePath)?.label || sourcePath;
    const value = SAMPLE_FIELD_VALUES[sourcePath] || 'Sample value';
    return `<br>${label}: ${value}`;
  }).join('');

// Mirrors the actual templates in backend/src/services/ncfService.js - keep these in sync if those change.
const buildSamplePreview = (emailType, selectedFields, fieldsList) => {
  const fieldLines = buildSampleFieldLines(selectedFields, fieldsList);
  switch (emailType) {
    case 'new':
      return {
        subject: `NCF submitted to HR: ${SAMPLE_CANDIDATE_NAME}`,
        body: `<p><strong>${SAMPLE_CANDIDATE_NAME}</strong> has been submitted for HR review.</p><p>${fieldLines}</p><p><a href="${SAMPLE_DETAIL_URL}">Open NCF</a></p>`
      };
    case 'updated':
      return {
        subject: `NCF update submitted to HR: ${SAMPLE_CANDIDATE_NAME}`,
        body: `<p><strong>${SAMPLE_CANDIDATE_NAME}</strong> has an update ready for HR review.</p><p>${fieldLines}<br>Changed fields: 2</p><p><a href="${SAMPLE_DETAIL_URL}">Open NCF</a></p>`
      };
    case 'review':
      return {
        subject: `NCF review requested: ${SAMPLE_CANDIDATE_NAME}`,
        body: `<p>Your NCF review is requested for <strong>${SAMPLE_CANDIDATE_NAME}</strong>.</p><p>${fieldLines}</p><p><a href="${SAMPLE_DETAIL_URL}">Open NCF for review</a></p>`
      };
    case 'start_date_reminder':
      return {
        subject: `Confirm start date: ${SAMPLE_CANDIDATE_NAME}`,
        body: `<p><strong>${SAMPLE_CANDIDATE_NAME}</strong> (${SAMPLE_CLIENT_NAME}) was scheduled to start on <strong>${SAMPLE_DATE}</strong>.</p><p>${fieldLines}</p><p>Please confirm the consultant started by verifying the start date on the NCF history page.</p><p><a href="${SAMPLE_DETAIL_URL}">Open NCF</a></p>`
      };
    case 'start_date_verified':
      return {
        subject: `NCF start date verified: ${SAMPLE_CANDIDATE_NAME}`,
        body: `<p><strong>${SAMPLE_CANDIDATE_NAME}</strong>'s start date of <strong>${SAMPLE_DATE}</strong> has been verified by Sarah Barnosky.</p><p>${fieldLines}</p><p><a href="${SAMPLE_DETAIL_URL}">Open NCF</a></p>`
      };
    case 'comment':
      return {
        subject: `New comment on NCF: ${SAMPLE_CANDIDATE_NAME}`,
        body: `<p><strong>Sarah Barnosky</strong> commented on the NCF for <strong>${SAMPLE_CANDIDATE_NAME}</strong>:</p><p>Can you confirm the client's billing address?</p><p>${fieldLines}</p><p><a href="${SAMPLE_DETAIL_URL}">Open NCF</a></p>`
      };
    default:
      return { subject: '', body: '' };
  }
};

function NcfSubmissionSettingsAdmin() {
  const [recipients, setRecipients] = useState([]);
  const [hrSubmissionEmail, setHrSubmissionEmail] = useState('');
  const [fields, setFields] = useState([]);
  const [emailFields, setEmailFields] = useState(DEFAULT_EMAIL_FIELDS);
  const [startDateReminderRecipients, setStartDateReminderRecipients] = useState({ recruiter: true, salesperson: true, hr: false });
  const [startDateReminderSchedule, setStartDateReminderSchedule] = useState(DEFAULT_REMINDER_SCHEDULE);
  const [emailType, setEmailType] = useState('new');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [draggedFieldIndex, setDraggedFieldIndex] = useState(null);
  const [runningReminders, setRunningReminders] = useState(false);
  const [reminderRunModal, setReminderRunModal] = useState({ show: false, success: false, title: '', message: '', details: [] });

  const load = async () => {
    try {
      const response = await ncfAPI.submissionSettings();
      setRecipients(response.data.settings?.recipients || []);
      setHrSubmissionEmail(response.data.settings?.hrSubmissionEmail || '');
      setFields(response.data.fields || []);
      setEmailFields(response.data.settings?.emailFields || DEFAULT_EMAIL_FIELDS);
      setStartDateReminderRecipients(response.data.settings?.startDateReminderRecipients || { recruiter: true, salesperson: true, hr: false });
      setStartDateReminderSchedule(response.data.settings?.startDateReminderSchedule || DEFAULT_REMINDER_SCHEDULE);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load submission settings.');
    }
  };

  useEffect(() => { load(); }, []);

  const searchDirectory = useCallback(async (query, { signal } = {}) => {
    const response = await ncfAPI.searchSubmissionDirectory(query, { signal });
    return (response.data.recipients || []).map(item => ({
      id: item.id,
      label: item.name || item.email,
      sublabel: `${item.email} • ${item.type === 'group' ? 'Group' : 'User'}`,
      raw: item
    }));
  }, []);

  const addRecipient = (option) => {
    if (!option?.raw || recipients.some(item => item.email.toLowerCase() === option.raw.email.toLowerCase())) return;
    setRecipients(previous => [...previous, option.raw]);
  };

  const removeRecipient = (email) => setRecipients(previous => previous.filter(item => item.email !== email));

  const addField = () => setEmailFields(previous => ({
    ...previous,
    [emailType]: [...(previous[emailType] || []), fields.find(field => !(previous[emailType] || []).includes(field.sourcePath))?.sourcePath || '']
  }));

  const updateField = (index, value) => setEmailFields(previous => ({
    ...previous,
    [emailType]: previous[emailType].map((field, fieldIndex) => fieldIndex === index ? value : field)
  }));

  const removeField = (index) => setEmailFields(previous => ({
    ...previous,
    [emailType]: previous[emailType].filter((_, fieldIndex) => fieldIndex !== index)
  }));

  const save = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const response = await ncfAPI.saveSubmissionSettings({ hrSubmissionEmail, recipients, emailFields, startDateReminderRecipients, startDateReminderSchedule });
      setStartDateReminderSchedule(response.data.settings?.startDateReminderSchedule || startDateReminderSchedule);
      setMessage('Submission settings saved.');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save submission settings.');
    } finally { setBusy(false); }
  };

  const toggleStartDateReminderRole = (role) => setStartDateReminderRecipients(previous => ({ ...previous, [role]: !previous[role] }));
  const setReminderScheduleField = (name, value) => setStartDateReminderSchedule(previous => ({ ...previous, [name]: value }));
  const toggleReminderWeekday = (weekday) => setStartDateReminderSchedule(previous => {
    const selected = previous.weekdays.includes(weekday);
    if (selected && previous.weekdays.length === 1) return previous;
    return {
      ...previous,
      weekdays: selected
        ? previous.weekdays.filter(day => day !== weekday)
        : [...previous.weekdays, weekday].sort((left, right) => left - right)
    };
  });

  const runStartDateRemindersNow = async () => {
    setRunningReminders(true); setError(''); setMessage('');
    try {
      const response = await ncfAPI.runStartDateReminders();
      const summary = response.data?.summary;
      if (summary?.skipped) {
        const messageText = 'A reminder run is already in progress.';
        setMessage(messageText);
        setReminderRunModal({ show: true, success: false, title: 'Reminder run not started', message: messageText, details: [] });
      } else {
        const failedResults = (summary?.results || []).filter(result => !result.ok);
        const dueCount = summary?.dueCount ?? 0;
        const pendingCount = summary?.pendingCount ?? dueCount;
        const messageText = `Reminder job ran: ${dueCount} reminder(s) sent or attempted.`;
        const details = [
          `Pending start-date verifications: ${pendingCount}`,
          `Reminders sent or attempted: ${dueCount}`,
          `Sent successfully: ${summary?.results?.filter(result => result.ok).length ?? 0}`,
          `Failed: ${failedResults.length}`,
          ...failedResults.map(result => `NCF ${result.ncfId}: ${result.error || 'Unknown error'}`)
        ];
        setMessage(messageText);
        setReminderRunModal({
          show: true,
          success: failedResults.length === 0,
          title: failedResults.length === 0 ? 'Reminder run completed' : 'Reminder run completed with errors',
          message: messageText,
          details
        });
      }
    } catch (err) {
      const responseData = err?.response?.data;
      const errorMessage = responseData?.error || err?.message || 'Could not run the start date reminder job.';
      const details = [
        err?.response?.status ? `HTTP status: ${err.response.status}` : '',
        responseData?.code ? `Error code: ${responseData.code}` : '',
        responseData?.details ? `Details: ${typeof responseData.details === 'string' ? responseData.details : JSON.stringify(responseData.details)}` : ''
      ].filter(Boolean);
      setError(errorMessage);
      setReminderRunModal({ show: true, success: false, title: 'Reminder run failed', message: errorMessage, details });
    } finally { setRunningReminders(false); }
  };

  const selectedFields = emailFields[emailType] || [];
  const availableFields = fields.filter(field => !selectedFields.includes(field.sourcePath));
  const samplePreview = buildSamplePreview(emailType, selectedFields, fields);
  const moveField = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
    setEmailFields(previous => {
      const nextFields = [...previous[emailType]];
      const [moved] = nextFields.splice(fromIndex, 1);
      nextFields.splice(toIndex, 0, moved);
      return { ...previous, [emailType]: nextFields };
    });
  };
  return (
    <section className="ncf-submission-settings-admin">
      {reminderRunModal.show && <div className="modal-overlay" onClick={() => setReminderRunModal(previous => ({ ...previous, show: false }))}><div className="modal-content" onClick={event => event.stopPropagation()}><div className="modal-header"><h2>{reminderRunModal.title}</h2><button type="button" className="close-btn" onClick={() => setReminderRunModal(previous => ({ ...previous, show: false }))}>×</button></div><p>{reminderRunModal.message}</p>{reminderRunModal.details.length > 0 && <ul>{reminderRunModal.details.map(detail => <li key={detail}>{detail}</li>)}</ul>}<div className="modal-actions"><button type="button" className="btn btn-primary" onClick={() => setReminderRunModal(previous => ({ ...previous, show: false }))}>Close</button></div></div></div>}
      <div className="ncf-section-header"><div><h2>Submission Settings</h2><p>Choose who receives NCF notifications and which fields appear in each email.</p></div><button className="btn btn-primary" type="button" onClick={save} disabled={busy}>Save settings</button></div>
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-info">{message}</div>}
      <section className="ncf-settings-block"><h3>HR submission recipients</h3><p className="form-hint">The primary address receives every NCF submission. Optional users or groups can be added below.</p><label className="ncf-primary-recipient">Primary HR email address<input type="email" value={hrSubmissionEmail} onChange={event => setHrSubmissionEmail(event.target.value)} placeholder="hr@company.com" /></label><p className="form-hint">Optional additional recipients</p><Typeahead value={null} onSelect={addRecipient} fetchOptions={searchDirectory} minChars={2} placeholder="Search users or groups..." />{recipients.length > 0 ? <div className="ncf-recipient-list">{recipients.map(item => <div className="ncf-recipient-row" key={`${item.type}-${item.email}`}><span><strong>{item.name}</strong><small>{item.email} • {item.type === 'group' ? 'Group' : 'User'}</small></span><button className="btn btn-danger btn-sm" type="button" onClick={() => removeRecipient(item.email)}>Remove</button></div>)}</div> : <p className="form-hint">No additional recipients configured.</p>}</section>
      <section className="ncf-settings-block"><h3>Email content fields</h3><div className="ncf-email-type-selector">{EMAIL_TYPES.map(type => <button type="button" key={type.key} className={emailType === type.key ? 'active' : ''} onClick={() => setEmailType(type.key)}>{type.label}</button>)}</div><p className="form-hint">The NCF link is included automatically and is not editable here.</p><div className="ncf-email-fields-list">{selectedFields.map((sourcePath, index) => <div className={`ncf-email-field-row${draggedFieldIndex === index ? ' is-dragging' : ''}`} key={`${emailType}-${index}`} draggable onDragStart={() => setDraggedFieldIndex(index)} onDragOver={event => event.preventDefault()} onDrop={() => { moveField(draggedFieldIndex, index); setDraggedFieldIndex(null); }} onDragEnd={() => setDraggedFieldIndex(null)}><span className="ncf-email-field-handle" title="Drag to reorder" aria-hidden="true">::</span><select value={sourcePath} onChange={event => updateField(index, event.target.value)}><option value="">Select a field...</option>{fields.filter(field => field.sourcePath === sourcePath || !selectedFields.includes(field.sourcePath)).map(field => <option key={field.sourcePath} value={field.sourcePath}>{field.label} ({field.sourcePath})</option>)}</select><button className="btn btn-danger btn-sm" type="button" onClick={() => removeField(index)}>Remove</button></div>)}</div><button className="btn btn-secondary" type="button" onClick={addField} disabled={availableFields.length === 0}>Add field</button>
        <div className="ncf-email-sample-preview">
          <h4>Sample email</h4>
          <p className="ncf-email-sample-subject"><strong>Subject:</strong> {samplePreview.subject}</p>
          <div className="ncf-email-sample-body" dangerouslySetInnerHTML={{ __html: samplePreview.body }} />
        </div>
      </section>
      <section className="ncf-settings-block">
        <h3>Start date reminders</h3>
        <p className="form-hint">Control when automated reminders run. Manual runs ignore this schedule.</p>
        <label className="ncf-checkbox-row ncf-reminder-enabled"><input type="checkbox" checked={Boolean(startDateReminderSchedule.enabled)} onChange={event => setReminderScheduleField('enabled', event.target.checked)} /> Send automated reminders</label>
        <div className="ncf-reminder-schedule-controls">
          <label>Cadence<span className="ncf-reminder-cadence"><span>Every</span><input type="number" min="1" max="30" value={startDateReminderSchedule.intervalDays} onChange={event => setReminderScheduleField('intervalDays', Math.min(30, Math.max(1, Number(event.target.value) || 1)))} disabled={!startDateReminderSchedule.enabled} /><span>day(s)</span></span></label>
          <label>Send time (Eastern)<input type="time" value={startDateReminderSchedule.timeEastern} onChange={event => setReminderScheduleField('timeEastern', event.target.value)} disabled={!startDateReminderSchedule.enabled} /></label>
        </div>
        <fieldset className="ncf-reminder-weekdays" disabled={!startDateReminderSchedule.enabled}>
          <legend>Delivery days</legend>
          <div>{WEEKDAYS.map(day => <label key={day.value}><input type="checkbox" checked={startDateReminderSchedule.weekdays.includes(day.value)} onChange={() => toggleReminderWeekday(day.value)} /> {day.label}</label>)}</div>
        </fieldset>
        <p className="form-hint">The consultant's submitter always receives the reminder. Choose who else is included.</p>
        <div className="ncf-checkbox-group">
          <label className="ncf-checkbox-row"><input type="checkbox" checked={Boolean(startDateReminderRecipients.recruiter)} onChange={() => toggleStartDateReminderRole('recruiter')} /> Recruiter</label>
          <label className="ncf-checkbox-row"><input type="checkbox" checked={Boolean(startDateReminderRecipients.salesperson)} onChange={() => toggleStartDateReminderRole('salesperson')} /> Salesperson</label>
          <label className="ncf-checkbox-row"><input type="checkbox" checked={Boolean(startDateReminderRecipients.hr)} onChange={() => toggleStartDateReminderRole('hr')} /> HR</label>
        </div>
        <button className="btn btn-secondary" type="button" onClick={runStartDateRemindersNow} disabled={runningReminders}>{runningReminders ? 'Running...' : 'Run now'}</button>
      </section>
    </section>
  );
}

export default NcfSubmissionSettingsAdmin;
